/**
 * Industries directory — filter pills, sort and "View All" for the
 * grid-industries-page section. Every entry is rendered server-side; this
 * element only decides which cards show and in what order. The first batch
 * (the section's "Cards shown before View All") shows; View All reveals the
 * rest, or only the next batch when "View All reveals" is set to that.
 *
 * Sort "newest" uses the metaobject ID, which rises as entries are created —
 * Liquid exposes no created/updated date for metaobjects.
 */
class IndustriesDirectory extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.grid-industries-page');
    this.grid = this.querySelector('#industries-grid');
    if (!this.grid) return;

    this.cards = Array.from(this.grid.querySelectorAll('.grid-industries-page__card'));
    this.pills = Array.from(this.querySelectorAll('.grid-industries-page__pill'));
    this.sortSelect = this.querySelector('[data-sort-select]');
    this.countEl = this.querySelector('#industries-count');
    this.emptyEl = this.querySelector('#industries-empty-filtered');
    this.moreWrap = this.querySelector('[data-more]');
    this.moreBtn = this.querySelector('[data-more-btn]');
    this.moreCount = this.querySelector('[data-more-count]');

    this.batch = Math.max(1, parseInt(this.section?.dataset.perPage || '8', 10));
    this.revealAll = (this.section?.dataset.viewAllMode || 'all') !== 'batch';
    this.sort = this.sortSelect?.value || this.section?.dataset.sort || 'newest';
    this.filter = 'all';
    this.shown = this.batch;

    // Visible strings are rendered from the locale file into data attributes,
    // with __PLACEHOLDER__ tokens filled in here.
    const d = this.dataset;
    this.text = {
      showing: d.textShowing || 'Showing __SHOWN__ of __TOTAL__ __NOUN__',
      showingAll: d.textShowingAll || 'Showing all __TOTAL__ __NOUN__',
      more: d.textMore || '(__COUNT__ more)',
      nounOne: d.textNounOne || 'industry',
      nounOther: d.textNounOther || 'industries',
    };

    this.onPillClick = this.onPillClick.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.onMore = this.onMore.bind(this);

    this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));
    this.sortSelect?.addEventListener('change', this.onSortChange);
    this.moreBtn?.addEventListener('click', this.onMore);

    this.populateCounts();
    this.render();
  }

  disconnectedCallback() {
    this.pills?.forEach((pill) => pill.removeEventListener('click', this.onPillClick));
    this.sortSelect?.removeEventListener('change', this.onSortChange);
    this.moreBtn?.removeEventListener('click', this.onMore);
  }

  onPillClick(event) {
    const pill = event.currentTarget;
    this.pills.forEach((p) => {
      p.classList.toggle('active', p === pill);
      p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
    });
    this.filter = (pill.dataset.filter || 'all').toLowerCase().trim();
    this.shown = this.batch;
    this.render();
  }

  onSortChange() {
    this.sort = this.sortSelect.value;
    this.shown = this.batch;
    this.render();
  }

  onMore() {
    const firstNew = this.shown;
    this.shown = this.revealAll ? this.cards.length : this.shown + this.batch;
    this.render();
    // Move focus to the first card just revealed, so keyboard and screen
    // reader users land on the new content rather than on a vanished button.
    const next = this.sorted(this.matched())[firstNew];
    const link = next?.querySelector('a');
    if (link) link.focus({ preventScroll: true });
  }

  matched() {
    return this.cards.filter((card) => {
      const tag = (card.dataset.cat || '').toLowerCase().trim();
      return this.filter === 'all' || tag === this.filter;
    });
  }

  sorted(list) {
    const byName = (a, b) =>
      (a.dataset.name || '').localeCompare(b.dataset.name || '', undefined, { sensitivity: 'base' });
    const byId = (a, b) => Number(a.dataset.id || 0) - Number(b.dataset.id || 0);
    const copy = list.slice();
    if (this.sort === 'oldest') return copy.sort(byId);
    if (this.sort === 'az') return copy.sort(byName);
    if (this.sort === 'za') return copy.sort((a, b) => byName(b, a));
    return copy.sort((a, b) => byId(b, a));
  }

  fill(template, values) {
    return Object.keys(values).reduce((out, key) => out.split(`__${key}__`).join(String(values[key])), template);
  }

  populateCounts() {
    this.querySelectorAll('[data-count-for]').forEach((el) => {
      const key = (el.getAttribute('data-count-for') || '').toLowerCase().trim();
      el.textContent =
        key === 'all'
          ? this.cards.length
          : this.cards.filter((c) => (c.dataset.cat || '').toLowerCase().trim() === key).length;
    });
  }

  render() {
    const list = this.sorted(this.matched());
    const total = list.length;
    const visible = new Set(list.slice(0, this.shown));

    // Reorder in the DOM so the visual order and the reading order agree.
    list.forEach((card) => this.grid.appendChild(card));
    this.cards.forEach((card) => {
      card.hidden = !visible.has(card);
    });

    const showing = Math.min(this.shown, total);
    const remaining = total - showing;

    if (this.countEl) {
      const noun = total === 1 ? this.text.nounOne : this.text.nounOther;
      const template = remaining > 0 ? this.text.showing : this.text.showingAll;
      this.countEl.textContent = this.fill(template, { SHOWN: showing, TOTAL: total, NOUN: noun });
    }

    if (this.moreWrap) this.moreWrap.hidden = remaining <= 0;
    if (this.moreCount) this.moreCount.textContent = remaining > 0 ? this.fill(this.text.more, { COUNT: remaining }) : '';

    if (this.emptyEl) this.emptyEl.hidden = total > 0;
    this.grid.hidden = total === 0;
  }
}

if (!customElements.get('industries-directory')) {
  customElements.define('industries-directory', IndustriesDirectory);
}
